import { useEffect } from "react";

// Lightweight, dependency-free head manager for SEO.
// Usage: useSeo({ title, description, image, type, noindex, jsonLd, canonical })
export const SITE_NAME = process.env.REACT_APP_SITE_NAME || "Tinh Anh";
export const SITE_URL = (process.env.REACT_APP_SITE_URL || window.location.origin).replace(/\/+$/, "");
export const DEFAULT_DESCRIPTION =
  process.env.REACT_APP_SITE_DESCRIPTION ||
  "Tinh Anh - products, news and photo gallery.";

const MANAGED = "data-seo";

// Strip HTML tags/entities and collapse whitespace; trim to `max` chars.
export const toPlainText = (html, max = 160) => {
  const text = String(html || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&[a-z#0-9]+;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
  return text.length > max ? text.slice(0, max - 1).trimEnd() + "…" : text;
};

export const absoluteUrl = (url) => {
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  return SITE_URL + (url.startsWith("/") ? url : "/" + url);
};

const setMeta = (attr, key, content) => {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!content) {
    if (el && el.hasAttribute(MANAGED)) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    el.setAttribute(MANAGED, "");
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const setLink = (rel, href) => {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    el.setAttribute(MANAGED, "");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

const setJsonLd = (data) => {
  document.head.querySelectorAll(`script[type="application/ld+json"][${MANAGED}]`).forEach((s) => s.remove());
  const list = Array.isArray(data) ? data : data ? [data] : [];
  list.forEach((d) => {
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.setAttribute(MANAGED, "");
    s.textContent = JSON.stringify(d);
    document.head.appendChild(s);
  });
};

export const applySeo = ({
  title,
  description,
  image,
  type = "website",
  noindex = false,
  jsonLd,
  canonical,
  lang,
} = {}) => {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : SITE_NAME;
  const desc = toPlainText(description || DEFAULT_DESCRIPTION);
  const url = absoluteUrl(canonical || window.location.pathname);
  const img = absoluteUrl(image || "/logo192.png");

  document.title = fullTitle;
  if (lang) document.documentElement.setAttribute("lang", lang);

  setMeta("name", "description", desc);
  setMeta("name", "robots", noindex ? "noindex, nofollow" : "index, follow");
  setLink("canonical", url);

  setMeta("property", "og:site_name", SITE_NAME);
  setMeta("property", "og:title", fullTitle);
  setMeta("property", "og:description", desc);
  setMeta("property", "og:type", type);
  setMeta("property", "og:url", url);
  setMeta("property", "og:image", img);
  if (lang) setMeta("property", "og:locale", lang === "vi" ? "vi_VN" : "en_US");

  setMeta("name", "twitter:card", image ? "summary_large_image" : "summary");
  setMeta("name", "twitter:title", fullTitle);
  setMeta("name", "twitter:description", desc);
  setMeta("name", "twitter:image", img);

  setJsonLd(jsonLd);
};

// React hook: re-applies whenever the serialized options change.
const useSeo = (options) => {
  const key = JSON.stringify(options || {});
  useEffect(() => {
    if (options === null) return; // caller not ready (e.g. still loading)
    applySeo(options);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
};

export default useSeo;
